import { ISortedPlanificationActivity } from '@sdk/contracts';
import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useActivityUIState } from './useActivityUIState';

import { isActivityCompleted, buildCourseUrl } from '@/utils';

interface PlanificationNavigationParams {
  sortedPlanificationActivitiesByDate: Record<string, ISortedPlanificationActivity[]>;
  currentActivityId?: string | null;
}

export const usePlanificationNavigation = ({
  sortedPlanificationActivitiesByDate,
  currentActivityId
}: PlanificationNavigationParams) => {
  const navigateTo = useNavigate();
  const [searchParams] = useSearchParams();

  const {
    isRecapModalOpen,
    setIsRecapModalOpen,
    showNextActivityShortcut,
    setShowNextActivityShortcut
  } = useActivityUIState();

  const planItemId = searchParams.get('planItemId');

  const getTaskPlanId = (task: ISortedPlanificationActivity) =>
    `${task.activity?._id}-${task.partIndex}`;

  const planificationTasks = useMemo(() => {
    return Object.keys(sortedPlanificationActivitiesByDate)
      .flatMap(date => sortedPlanificationActivitiesByDate[date] ?? [])
      .filter(task => !!task.activity?._id && !!task.subscription?._id && !!task.section?._id);
  }, [sortedPlanificationActivitiesByDate]);

  const resolveCurrentIndex = (): number => {
    if (planItemId) {
      return planificationTasks.findIndex(task => getTaskPlanId(task) === planItemId);
    }
    if (currentActivityId) {
      return planificationTasks.findIndex(task => task.activity?._id === currentActivityId);
    }
    return -1;
  };

  const currentIndex = resolveCurrentIndex();
  const currentActivityOfIndex =
    currentIndex >= 0 ? planificationTasks[currentIndex]?.activity?._id : currentActivityId ?? null;

  const getPlanificationNextTrack = () => {
    if (planificationTasks.length === 0 || currentIndex === -1) return null;

    for (let i = currentIndex + 1; i < planificationTasks.length; i++) {
      const task = planificationTasks[i];
      const id = task.activity?._id;
      const subscriptionId = task.subscription?._id;
      const courseId = task.subscription?.course?._id;
      const sectionId = task.section?._id;

      if (!id || !subscriptionId || !courseId || !sectionId) continue;
      if (currentActivityOfIndex && id === currentActivityOfIndex) continue;
      if (task.subscription && isActivityCompleted(id, task.subscription)) continue;

      return {
        _id: getTaskPlanId(task),
        subscription: subscriptionId,
        course: courseId,
        section: sectionId,
        activity: id
      };
    }
    return null;
  };

  const getPlanificationPreviousTrack = () => {
    if (planificationTasks.length === 0 || currentIndex === -1) return null;

    for (let i = currentIndex - 1; i >= 0; i--) {
      const task = planificationTasks[i];
      const id = task.activity?._id;
      const subscriptionId = task.subscription?._id;
      const courseId = task.subscription?.course?._id;
      const sectionId = task.section?._id;

      if (!id || !subscriptionId || !courseId || !sectionId) continue;

      return {
        _id: getTaskPlanId(task),
        subscription: subscriptionId,
        course: courseId,
        section: sectionId,
        activity: id
      };
    }
    return null;
  };

  const nextActivity = getPlanificationNextTrack();
  const previousActivity = getPlanificationPreviousTrack();

  const navigateToNextActivity = () => {
    if (!nextActivity) return;
    if (showNextActivityShortcut) setShowNextActivityShortcut(false);
    if (isRecapModalOpen) setIsRecapModalOpen(false);

    navigateTo(
      buildCourseUrl({
        subscriptionId: nextActivity.subscription,
        activityId: nextActivity.activity,
        planItemId: nextActivity._id,
        fromPath: searchParams.get('from') ?? undefined,
        isPlanificationFlow: true,
        showPlanificationIntro: false
      })
    );
  };

  const navigateToPreviousActivity = () => {
    if (!previousActivity) return;
    if (showNextActivityShortcut) setShowNextActivityShortcut(false);
    if (isRecapModalOpen) setIsRecapModalOpen(false);

    navigateTo(
      buildCourseUrl({
        subscriptionId: previousActivity.subscription,
        activityId: previousActivity.activity,
        planItemId: previousActivity._id,
        fromPath: searchParams.get('from') ?? undefined,
        isPlanificationFlow: true,
        showPlanificationIntro: false
      })
    );
  };

  return { nextActivity, previousActivity, navigateToNextActivity, navigateToPreviousActivity };
};
